import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { List, X, HandHeart, UserCircle, CaretDown } from "@phosphor-icons/react";
import { NAV_MENU } from "../lib/site";
import { useAuth } from "../lib/auth";

// -------- helpers --------
const isActiveFor = (pathname, item) => {
  if (item.type === "link") {
    return item.to === "/" ? pathname === "/" : pathname.startsWith(item.to);
  }
  // Dropdown parent is only active when the parent's own `to` or a non-anchor
  // child page matches. Anchor-only children (e.g. /classes#workshops) do not
  // claim the parent because they merely deep-link into another section.
  const targets = [
    item.to,
    ...(item.items || [])
      .filter((i) => i.to && !i.to.includes("#"))
      .map((i) => i.to),
  ].filter(Boolean);
  return targets.some((t) =>
    t === "/" ? pathname === "/" : pathname === t || pathname.startsWith(`${t}/`)
  );
};

// -------- Desktop dropdown --------
const DesktopDropdown = ({ item, pathname }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const active = isActiveFor(pathname, item);

  const clearClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    clearClose();
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  // Close whenever the app navigates to a new path.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Close when clicking outside
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (
        !triggerRef.current?.contains(e.target) &&
        !menuRef.current?.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Keyboard nav inside the submenu
  const onTriggerKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      requestAnimationFrame(() => {
        const first = menuRef.current?.querySelector("[data-menu-item]");
        first?.focus();
      });
    } else if (e.key === "Enter" || e.key === " ") {
      // If parent has its own `to`, Enter/Space navigates (default behavior for
      // <Link>). Only preventDefault when there is no parent route.
      if (!item.to) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    }
  };
  const onMenuKeyDown = (e) => {
    const items = Array.from(menuRef.current?.querySelectorAll("[data-menu-item]") || []);
    const idx = items.indexOf(document.activeElement);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      items[(idx + 1) % items.length]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      items[(idx - 1 + items.length) % items.length]?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      items[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      items[items.length - 1]?.focus();
    }
  };

  const Trigger = item.to ? Link : "button";
  const triggerProps = item.to
    ? { to: item.to }
    : { type: "button", onClick: () => setOpen((v) => !v) };

  return (
    <div
      className="relative"
      onMouseEnter={() => {
        clearClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
      onFocus={() => {
        clearClose();
        setOpen(true);
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) scheduleClose();
      }}
    >
      <Trigger
        {...triggerProps}
        ref={triggerRef}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={`nav-trigger-${item.id}`}
        onKeyDown={onTriggerKeyDown}
        className={`inline-flex items-center gap-1 px-3.5 py-2 text-sm font-medium rounded-full transition-colors ${
          active ? "text-primary" : "text-ink-700 hover:text-primary"
        }`}
      >
        <span className="relative">
          {item.label}
          {active && (
            <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-secondary" />
          )}
        </span>
        <CaretDown
          size={12}
          weight="bold"
          className={`transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </Trigger>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-label={item.label}
          data-testid={`nav-menu-${item.id}`}
          onKeyDown={onMenuKeyDown}
          className="absolute left-0 top-full pt-2 min-w-[260px] z-50"
        >
          <div className="bg-white rounded-2xl border border-ink-100 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.18)] p-2">
            {item.items.map((child) => (
              <NavLink
                key={child.id}
                to={child.to}
                data-menu-item
                data-testid={`nav-item-${child.id}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-2.5 text-sm rounded-xl transition-colors ${
                    isActive
                      ? "bg-primary/5 text-primary font-semibold"
                      : "text-ink-800 hover:bg-ink-50 hover:text-primary"
                  }`
                }
              >
                {child.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// -------- Mobile accordion group --------
const MobileGroup = ({ item, pathname, onNavigate }) => {
  const [expanded, setExpanded] = useState(() => isActiveFor(pathname, item));
  const active = isActiveFor(pathname, item);

  return (
    <div className="border-b border-ink-100 last:border-b-0">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
        data-testid={`nav-m-group-${item.id}`}
        className={`w-full flex items-center justify-between px-3 py-3.5 text-base font-medium text-left ${
          active ? "text-primary" : "text-ink-800"
        }`}
      >
        <span>{item.label}</span>
        <CaretDown
          size={16}
          weight="bold"
          className={`transition-transform text-ink-500 ${expanded ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {expanded && (
        <div
          className="pb-2 pl-3 space-y-0.5"
          data-testid={`nav-m-group-panel-${item.id}`}
        >
          {item.items.map((child) => (
            <NavLink
              key={child.id}
              to={child.to}
              onClick={onNavigate}
              data-testid={`nav-m-item-${child.id}`}
              className={({ isActive }) =>
                `block px-3 py-2.5 rounded-lg text-[15px] ${
                  isActive
                    ? "bg-primary/5 text-primary font-semibold"
                    : "text-ink-700 hover:bg-ink-50"
                }`
              }
            >
              {child.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

// -------- Navbar --------
const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  const closeMobile = useCallback(() => setOpen(false), []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-md border-b border-ink-100 shadow-[0_2px_20px_rgba(15,23,42,0.04)]"
          : "bg-white/70 backdrop-blur-sm"
      }`}
      data-testid="site-navbar"
    >
      <div className="container-x flex items-center justify-between h-20 gap-4">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-2.5 group shrink-0"
        >
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary group-hover:bg-primary/15 transition-colors">
            <HandHeart size={22} weight="duotone" />
          </span>
          <span className="leading-tight">
            <span className="block font-heading text-xl text-ink-900 font-medium">
              Begin Again
            </span>
            <span className="block text-[10px] uppercase tracking-[0.22em] text-primary font-semibold">
              Living Ministries
            </span>
          </span>
        </Link>

        <nav
          className="hidden xl:flex items-center gap-0.5"
          data-testid="nav-desktop"
          aria-label="Primary"
        >
          {NAV_MENU.map((item) =>
            item.type === "link" ? (
              <NavLink
                key={item.id}
                to={item.to}
                data-testid={`nav-link-${item.id}`}
                className={({ isActive }) =>
                  `relative px-3.5 py-2 text-sm font-medium transition-colors rounded-full ${
                    isActive ? "text-primary" : "text-ink-700 hover:text-primary"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    {isActive && (
                      <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-secondary" />
                    )}
                  </>
                )}
              </NavLink>
            ) : (
              <DesktopDropdown
                key={item.id}
                item={item}
                pathname={location.pathname}
              />
            )
          )}
        </nav>

        <div className="hidden xl:flex items-center gap-2 shrink-0">
          {user?.is_admin && (
            <Link
              to="/admin"
              data-testid="nav-admin-link"
              className="inline-flex items-center gap-1.5 text-primary text-sm font-semibold px-3 py-2 rounded-full hover:bg-primary/5 transition-colors"
            >
              <UserCircle size={18} weight="duotone" />
              Admin
            </Link>
          )}
          <Link
            to="/donate"
            data-testid="nav-donate-btn"
            className="inline-flex items-center gap-2 bg-secondary text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-secondary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
          >
            <HandHeart size={16} weight="fill" />
            Give
          </Link>
        </div>

        <button
          className="xl:hidden inline-flex items-center justify-center w-11 h-11 rounded-full text-ink-800 hover:bg-ink-100"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={22} /> : <List size={22} />}
        </button>
      </div>

      {open && (
        <div
          className="xl:hidden border-t border-ink-100 bg-white max-h-[calc(100vh-5rem)] overflow-y-auto"
          data-testid="nav-mobile-panel"
        >
          <div className="container-x py-4">
            {NAV_MENU.map((item) =>
              item.type === "link" ? (
                <NavLink
                  key={item.id}
                  to={item.to}
                  onClick={closeMobile}
                  data-testid={`nav-m-link-${item.id}`}
                  className={({ isActive }) =>
                    `block px-3 py-3.5 text-base font-medium border-b border-ink-100 last:border-b-0 ${
                      isActive
                        ? "text-primary"
                        : "text-ink-800 hover:text-primary"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ) : (
                <MobileGroup
                  key={item.id}
                  item={item}
                  pathname={location.pathname}
                  onNavigate={closeMobile}
                />
              )
            )}

            <Link
              to="/donate"
              onClick={closeMobile}
              data-testid="nav-m-donate-btn"
              className="mt-5 inline-flex w-full items-center justify-center gap-2 bg-secondary text-white rounded-full px-5 py-3.5 font-semibold hover:bg-secondary-dark transition-colors"
            >
              <HandHeart size={18} weight="fill" />
              Give to the Ministry
            </Link>
            {user?.is_admin && (
              <Link
                to="/admin"
                onClick={closeMobile}
                data-testid="nav-m-admin-link"
                className="mt-3 inline-flex w-full items-center justify-center gap-2 border-2 border-primary text-primary rounded-full px-5 py-3 font-semibold"
              >
                <UserCircle size={18} weight="duotone" />
                Admin Dashboard
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
